import { useMediaQuery } from "@react-hooks-library/core";
import { Blac } from "blac-next";
import type { FC } from "react";
import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Path from "src/constants/path";
import { KnownProgram } from "src/constants/programs";
import { createAppPath } from "src/lib/createAppPath";
import AppointmentsBloc from "src/state/AppointmentsBloc/AppointmentsBloc";
import TaskManagementBloc from "src/state/TaskManagementBloc/TaskManagementBloc";
import ProfileDialog from "src/ui/components/ProfileOverview/ProfileDialog";
import ProfileOverview from "src/ui/components/ProfileOverview/ProfileOverview";
import ProfileRouter from "src/ui/components/ProfileOverview/ProfileRouter";
import AppTopSpacer from "src/ui/components/StyledComponents/AppTopSpacer";
import DashboardPage from "src/ui/pages/DashboardPage/DashboardPage";
import { APP_BREAKPOINT } from "src/constants/layout";
import { isHybridApp } from "src/lib/platform";

interface HomePageProps {
  showProfile?: boolean;
}

const HomePage: FC<HomePageProps> = ({ showProfile = false }) => {
  const isDesktop = useMediaQuery(`(min-width: ${APP_BREAKPOINT}px)`);
  const location = useLocation();
  const navigate = useNavigate();

  const profileRootPath = createAppPath(Path.profile);
  const isProfileSubRoute =
    showProfile && location.pathname !== profileRootPath;

  const profileParentPath = (() => {
    const parent = location.pathname.split("/").slice(0, -1).join("/");
    return parent.startsWith(profileRootPath) ? parent : profileRootPath;
  })();

  useEffect(() => {
    void Blac.getBloc(AppointmentsBloc).loadAppointments();
    void Blac.getBloc(TaskManagementBloc).loadProgramTasks(
      KnownProgram.ONBOARDING
    );
  }, []);

  // Ref so the close handler reads the latest value when called
  // asynchronously by Ionic's onWillDismiss.
  const isDesktopRef = useRef(isDesktop);
  isDesktopRef.current = isDesktop;

  const handleProfileClose = () => {
    if (!isDesktopRef.current) {
      navigate(createAppPath(Path.home));
    }
  };

  const showAsModal = showProfile && !isDesktop;

  return (
    <>
      {isDesktop && showProfile ? (
        <>
          {!isHybridApp() && <AppTopSpacer pt />}
          {isProfileSubRoute ? <ProfileRouter /> : <ProfileOverview />}
        </>
      ) : (
        <DashboardPage />
      )}
      <ProfileDialog
        isOpen={showAsModal}
        onClose={handleProfileClose}
        onBack={
          isProfileSubRoute ? () => navigate(profileParentPath) : undefined
        }
        hideHeaderOnMobile={!isProfileSubRoute}
      >
        {showAsModal ? (
          isProfileSubRoute ? (
            <ProfileRouter />
          ) : (
            <ProfileOverview onClose={handleProfileClose} />
          )
        ) : null}
      </ProfileDialog>
    </>
  );
};

export default HomePage;
